import { styled } from "@pigment-css/react";
import { RadioGroup } from "radix-ui";
import { useTranslations } from "@acto/web-common/locales";
import type Meeting from "@acto/taskpane/core/domain/Meeting";
import {
  NonScalingMeetingRow,
  MeetingRowAddress,
  MeetingRowInfo,
  MeetingRowName,
  MeetingTimeBox,
  formatMeetingRowAddress,
} from "@acto/taskpane/field-sales-2/components/primitives/MeetingRow";
import { formatHourMinute } from "@acto/taskpane/field-sales-2/utils/format";
import { RadioCircle } from "@acto/taskpane/field-sales-2/components/primitives/SheetOptions";

const OptionsList = styled(RadioGroup.Root)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
}));

const OptionRadio = styled(RadioCircle)({
  marginLeft: "auto",
  alignSelf: "center",
});

interface ReferenceMeetingPickerProps {
  meetings: Meeting[];
  selectedMeetingId: string;
  onSelect: (meeting: Meeting) => void;
}

export function ReferenceMeetingPicker({ meetings, selectedMeetingId, onSelect }: ReferenceMeetingPickerProps) {
  const t = useTranslations("fieldSalesTwo.nearby");

  return (
    <OptionsList
      value={selectedMeetingId}
      onValueChange={(meetingId) => {
        const meeting = meetings.find((candidate) => candidate.id === meetingId);
        if (meeting) onSelect(meeting);
      }}
      aria-label={t("listHeader")}
    >
      {meetings.map((meeting) => {
        const address = formatMeetingRowAddress(meeting);
        return (
          <RadioGroup.Item key={meeting.id} value={meeting.id} asChild>
            <NonScalingMeetingRow type="button">
              <MeetingTimeBox>
                {meeting.scheduledStartTime ? formatHourMinute(meeting.scheduledStartTime) : ""}
              </MeetingTimeBox>
              <MeetingRowInfo>
                <MeetingRowName>{meeting.customer.name}</MeetingRowName>
                {address && <MeetingRowAddress>{address}</MeetingRowAddress>}
              </MeetingRowInfo>
              <OptionRadio aria-hidden="true" />
            </NonScalingMeetingRow>
          </RadioGroup.Item>
        );
      })}
    </OptionsList>
  );
}
